import { useState, useEffect } from 'react';
import { useToast } from '../../../shared/ui/Toast/ToastProvider';
import { getErrorMessage } from '../../../shared/errors/getErrorMessage';
import { useColumnWidths, useSaveColumnWidths } from './useComponentTaskData';

// Extraído de `ComponentTaskHoy.jsx` (02.1-b6-taskhoy-orchestrator.md, paso C2) -- resize de
// columnas de `TaskListView`, con persistencia en BD. Autocontenido: no depende de `activeTask`
// ni de ningún otro subsistema del container.
// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: combina los anchos de columna guardados (query) con el estado local de anchos.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
export function useTaskColumnResize({ userId }) {
    const toast = useToast();
    const saveColumnWidthsMutation = useSaveColumnWidths(userId);

    const [columnWidths, setColumnWidths] = useState({
        stage: 120,
        title: 250,
        date: 110,
        tiempo: 90,
        priority: 90,
        assignee: 110,
        supervisor: 100
    });
    const [resizingCol, setResizingCol] = useState(null);
    const [resizingColStartX, setResizingColStartX] = useState(0);
    const [resizingColStartWidth, setResizingColStartWidth] = useState(0);

    // Load column width customizations from database
    const { data: savedColumnWidths } = useColumnWidths(userId);
    useEffect(() => {
        if (savedColumnWidths) {
            setColumnWidths(prev => ({ ...prev, ...savedColumnWidths }));
        }
    }, [savedColumnWidths]);

    const handleColResizeStart = (e, col) => {
        e.stopPropagation();
        e.preventDefault();
        setResizingCol(col);
        setResizingColStartX(e.clientX);
        setResizingColStartWidth(columnWidths[col] || 100);
        try {
            e.target.setPointerCapture(e.pointerId);
        } catch(err) {}
    };

    const handleColResizeMove = (e) => {
        if (!resizingCol) return;
        const deltaX = e.clientX - resizingColStartX;
        const newWidth = Math.max(50, resizingColStartWidth + deltaX);
        setColumnWidths(prev => ({
            ...prev,
            [resizingCol]: newWidth
        }));
    };

    const handleColResizeEnd = async (e, pointerId) => {
        if (!resizingCol) return;
        try {
            e.target.releasePointerCapture(pointerId);
        } catch(err) {}

        const finalCol = resizingCol;
        setResizingCol(null);

        // Persist all column widths in database
        if (userId) {
            const finalWidths = {
                ...columnWidths,
                [finalCol]: columnWidths[finalCol]
            };
            try {
                await saveColumnWidthsMutation.mutateAsync(finalWidths);
            } catch(err) {
                console.error("Error saving column widths:", err);
                toast.error(getErrorMessage(err));
            }
        }
    };

    return {
        columnWidths, resizingCol,
        handleColResizeStart, handleColResizeMove, handleColResizeEnd,
    };
}
